import React, { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import carBikeImage from "../../assets/car-bike.png";
import styles from "./AdhaarVerification.module.css";

function AdhaarVerification() {
  const navigate = useNavigate();
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const streamRef = useRef(null);
  const fileInputRefs = useRef({
    rcFront: null,
    rcBack: null,
    aadharFront: null,
    aadharBack: null,
  });
  
  const [formData, setFormData] = useState({
    name: "",   
    registeredMobileNumber: "",
  });
  const [files, setFiles] = useState({
    rcFront: null,
    rcBack: null,
    aadharFront: null,
    aadharBack: null,
  });
  const [previews, setPreviews] = useState({
    rcFront: null,
    rcBack: null,
    aadharFront: null,
    aadharBack: null,
  });
  const [showPopup, setShowPopup] = useState(false);
  const [error, setError] = useState(null);
  const [showCamera, setShowCamera] = useState(false);
  const [currentCaptureType, setCurrentCaptureType] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [cameraSupported, setCameraSupported] = useState(true);
  const [uploadProgress, setUploadProgress] = useState({});

  // Check camera support on component mount
  useEffect(() => {
    checkCameraSupport();
    
    // Cleanup on unmount
    return () => {
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(track => track.stop());
      }
    };
  }, []);

  const checkCameraSupport = () => {
    // Check if we're in a secure context (HTTPS or localhost)
   const isSecureContext =
  window.isSecureContext ||
  window.location.protocol === 'https:' ||
  window.location.hostname === 'localhost';

    // Check if getUserMedia is available
    const hasGetUserMedia = !!(navigator.mediaDevices && navigator.mediaDevices.getUserMedia) ||
                            !!(navigator.getUserMedia || navigator.webkitGetUserMedia || navigator.mozGetUserMedia);
    
    const supported = isSecureContext && hasGetUserMedia;
    setCameraSupported(supported);
    
    if (!supported) {
      console.warn('Camera not supported:', { isSecureContext, hasGetUserMedia });
    }
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const createFilePreview = (file) => {
    return new Promise((resolve) => {
      if (file && file.type.startsWith('image/')) {
        const reader = new FileReader();
        reader.onload = (e) => resolve(e.target.result);
        reader.readAsDataURL(file);
      } else {
        resolve(null);
      }
    });
  };

  const handleFileChange = async (e, fileType) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file size (10MB limit for frontend)
      if (file.size > 10 * 1024 * 1024) {
        setError(`File ${file.name} is too large. Maximum size is 10MB.`);
        return;
      }

      // Validate file type
      const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/bmp', 'image/webp', 'image/heic', 'image/heif'];
      if (!allowedTypes.includes(file.type.toLowerCase())) {
        setError(`File type ${file.type} is not supported. Please upload JPG, PNG, GIF, BMP, WebP, HEIC, or HEIF files.`);
        return;
      }

      setFiles((prevFiles) => ({ ...prevFiles, [fileType]: file }));
      
      // Create preview
      const preview = await createFilePreview(file);
      setPreviews((prevPreviews) => ({ ...prevPreviews, [fileType]: preview }));
      
      setError(null);
    }
  };

  const getUserMediaWithFallback = async (constraints) => {
    // Modern way
    if (navigator.mediaDevices && navigator.mediaDevices.getUserMedia) {
      return await navigator.mediaDevices.getUserMedia(constraints);
    }
    
    // Fallback for older browsers
    const getUserMedia = navigator.getUserMedia || 
                        navigator.webkitGetUserMedia || 
                        navigator.mozGetUserMedia;
    
    if (getUserMedia) {
      return new Promise((resolve, reject) => {
        getUserMedia.call(navigator, constraints, resolve, reject);
      });
    }
    
    throw new Error('getUserMedia is not supported in this browser');
  };

  const startCamera = async (fileType) => {
    try {
      setError(null);
      setCurrentCaptureType(fileType);

      // Check if camera is supported
      if (!cameraSupported) {
        setError("Camera is not supported in this browser or context. Please use file upload instead.");
        return;
      }

      setShowCamera(true);

      // Camera constraints with multiple fallbacks
      const constraints = [
        // Try back camera first (better for documents)
        { 
          video: { 
            facingMode: { exact: 'environment' },
            width: { ideal: 1920, min: 640 },
            height: { ideal: 1080, min: 480 },
            aspectRatio: 16/9
          } 
        },
        // Fallback to back camera without exact constraint
        { 
          video: { 
            facingMode: 'environment',
            width: { ideal: 1280, min: 640 },
            height: { ideal: 720, min: 480 }
          } 
        },
        // Try front camera
        { 
          video: { 
            facingMode: 'user',
            width: { ideal: 1280, min: 640 },
            height: { ideal: 720, min: 480 }
          } 
        },
        // Any available camera
        { 
          video: {
            width: { ideal: 1280, min: 640 },
            height: { ideal: 720, min: 480 }
          } 
        },
        // Minimal constraints
        { video: true }
      ];

      let stream = null;
      let lastError = null;

      for (const constraint of constraints) {
        try {
          console.log('Trying camera constraint:', constraint);
          stream = await getUserMediaWithFallback(constraint);
          if (stream) {
            console.log('Successfully got camera stream with constraint:', constraint);
            break;
          }
        } catch (err) {
          console.log('Camera constraint failed:', constraint, err);
          lastError = err;
          continue;
        }
      }

      if (!stream) {
        throw lastError || new Error('No camera stream available');
      }

      streamRef.current = stream;
      
      // Wait for video element and set up properly
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        
        // Handle video events
        const handleVideoLoad = () => {
          console.log("Video metadata loaded");
          const playPromise = videoRef.current.play();
          if (playPromise !== undefined) {
            playPromise.catch(playError => {
              console.error("Play error:", playError);
              setError("Failed to start video playback. Please allow camera permissions.");
            });
          }
        };

        const handleVideoReady = () => {
          console.log("Video can play");
          // Hide loading indicator
          const loading = document.querySelector(`.${styles.cameraLoading}`);
          if (loading) loading.style.display = 'none';
        };

        const handleVideoError = (videoError) => {
          console.error("Video error:", videoError);
          setError("Video playback error. Please try again.");
        };

        videoRef.current.onloadedmetadata = handleVideoLoad;
        videoRef.current.oncanplay = handleVideoReady;
        videoRef.current.onerror = handleVideoError;

        // Set video attributes for mobile compatibility
        videoRef.current.setAttribute('playsinline', 'true');
        videoRef.current.setAttribute('webkit-playsinline', 'true');
        videoRef.current.muted = true;
        videoRef.current.autoplay = true;
      }

    } catch (err) {
      console.error("Camera access error:", err);
      setShowCamera(false);
      
      let errorMessage = "Camera access failed. ";
      
      if (err.name === 'NotAllowedError' || err.name === 'PermissionDeniedError') {
        errorMessage += "Please allow camera permissions and try again.";
      } else if (err.name === 'NotFoundError' || err.name === 'DevicesNotFoundError') {
        errorMessage += "No camera found on this device.";
      } else if (err.name === 'NotReadableError' || err.name === 'TrackStartError') {
        errorMessage += "Camera is being used by another application.";
      } else if (err.name === 'OverconstrainedError' || err.name === 'ConstraintNotSatisfiedError') {
        errorMessage += "Camera constraints not supported.";
      } else if (err.name === 'NotSupportedError') {
        errorMessage += "Camera not supported in this browser.";
      } else if (err.name === 'SecurityError') {
        errorMessage += "Camera access blocked due to security restrictions.";
      } else {
        errorMessage += `${err.message}`;
      }
      
      errorMessage += " Please use file upload instead.";
      setError(errorMessage);
    }
  };

  const capturePhoto = () => {
    const video = videoRef.current;
    const canvas = canvasRef.current;
    
    if (!video || !canvas) {
      setError("Camera not ready. Please try again.");
      return;
    }

    // Check if video has loaded and has dimensions
    if (video.videoWidth === 0 || video.videoHeight === 0) {
      setError("Video not ready. Please wait for camera to load completely.");
      return;
    }

    try {
      const context = canvas.getContext('2d');

      // Set canvas dimensions to match video
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;

      // Draw the video frame to canvas
      context.drawImage(video, 0, 0, canvas.width, canvas.height);

      // Convert canvas to blob with high quality
      canvas.toBlob(async (blob) => {
        if (blob && blob.size > 0) {
          // Create a file from the blob
          const timestamp = new Date().getTime();
          const file = new File([blob], `${currentCaptureType}_captured_${timestamp}.jpg`, {
            type: 'image/jpeg',
            lastModified: timestamp
          });

          // Update files state
          setFiles((prevFiles) => ({
            ...prevFiles,
            [currentCaptureType]: file
          }));

          // Create preview
          const preview = await createFilePreview(file);
          setPreviews((prevPreviews) => ({ 
            ...prevPreviews, 
            [currentCaptureType]: preview 
          }));

          // Close camera
          closeCamera();
          setError(null);
        } else {
          setError("Failed to capture photo. Please try again.");
        }
      }, 'image/jpeg', 0.95); // High quality JPEG
    } catch (err) {
      console.error("Capture error:", err);
      setError("Failed to capture photo. Please try again.");
    }
  };

  const closeCamera = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => {
        track.stop();
        console.log('Camera track stopped:', track.kind);
      });
      streamRef.current = null;
    }
    
    if (videoRef.current) {
      videoRef.current.srcObject = null;
      videoRef.current.onloadedmetadata = null;
      videoRef.current.oncanplay = null;
      videoRef.current.onerror = null;
    }
    
    setShowCamera(false);
    setCurrentCaptureType(null);
  };

  const removeFile = (fileType) => {
    setFiles(prev => ({...prev, [fileType]: null}));
    setPreviews(prev => ({...prev, [fileType]: null}));
    
    // Clear file input
    if (fileInputRefs.current[fileType]) {
      fileInputRefs.current[fileType].value = '';
    }
  };

const handleSubmit = async () => {
  if (isSubmitting) return;

  // Hardcoded values
  const token = "eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpZCI6IjI5ZDYwNjRiLTFkZGItNDg2ZC1hMGQ3LTE0ZGQ2MTRhZjA2OCIsImlhdCI6MTc1NTUxMjE2OSwiZXhwIjoxNzU4MTA0MTY5fQ.QULoq5XM4S5a2S_DDG6Ed2H-KjjlnQ9AGdVQ0q_oC-E";
  const vehicle_id = "138f9d80-14fc-47c6-bce9-a21a4b4b3f28";
  
  // Validate form data
  if (!formData.name.trim()) {
    setError("Please enter your name.");
    return;
  }
  
  if (!formData.registeredMobileNumber.trim()) {
    setError("Please enter your registered mobile number.");
    return;
  }
  
  if (!/^[6-9]\d{9}$/.test(formData.registeredMobileNumber)) {
    setError("Please enter a valid 10-digit mobile number.");
    return;
  }

  // Check if all required documents are uploaded
  const requiredFiles = ['rcFront', 'rcBack', 'aadharFront', 'aadharBack'];
  const missingFiles = requiredFiles.filter(fileType => !files[fileType]);
  
  if (missingFiles.length > 0) {
    setError(`Please upload all required documents: ${missingFiles.map(f => 
      f.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())
    ).join(', ')}`);
    return;
  }

  // Check individual file sizes (50MB limit each for frontend)
  const fileSizeLimit = 50 * 1024 * 1024; // 50MB
  for (const [key, file] of Object.entries(files)) {
    if (file && file.size > fileSizeLimit) {
      setError(`File "${file.name}" is too large. Maximum size is 50MB per file.`);
      return;
    }
  }

  // Check total file size (150MB total)
  const totalSize = Object.values(files)
    .filter(Boolean)
    .reduce((sum, file) => sum + file.size, 0);
  
  if (totalSize > 150 * 1024 * 1024) {
    setError(`Total file size too large (${(totalSize / 1024 / 1024).toFixed(1)}MB). Maximum combined size is 150MB.`);
    return;
  }

  setIsSubmitting(true);
  setError(null);
  setUploadProgress({ overall: 0 });

  try {
    const formDataToSend = new FormData();
    formDataToSend.append("service_type", "AdhaarVerification");
    formDataToSend.append("vehicle_id", vehicle_id);
    formDataToSend.append(
      "additional_info",
      JSON.stringify({
        registered_mobile_number: formData.registeredMobileNumber,
        name: formData.name,
      })
    );
    
    // Append all files
    Object.entries(files).forEach(([key, file]) => {
      if (file) {
        formDataToSend.append("documents", file, file.name);
        console.log(`Appending file: ${key}, Name: ${file.name}, Size: ${(file.size / 1024 / 1024).toFixed(2)}MB, Type: ${file.type}`);
      }
    });

    console.log(`Total files to upload: ${Object.values(files).filter(Boolean).length}`);
    console.log(`Total size: ${(totalSize / 1024 / 1024).toFixed(2)}MB`);

    // Use XMLHttpRequest for better error handling and progress
    const xhr = new XMLHttpRequest();
    
    const uploadPromise = new Promise((resolve, reject) => {
      // Upload progress
      xhr.upload.onprogress = (event) => {
        if (event.lengthComputable) {
          const percentComplete = (event.loaded / event.total) * 100;
          setUploadProgress(prev => ({ 
            ...prev, 
            overall: Math.round(percentComplete) 
          }));
          console.log(`Upload progress: ${percentComplete.toFixed(1)}%`);
        }
      };

      // Success handler
      xhr.onload = () => {
        console.log(`Upload completed with status: ${xhr.status}`);
        if (xhr.status >= 200 && xhr.status < 300) {
          try {
            const response = JSON.parse(xhr.response);
            resolve(response);
          } catch (e) {
            resolve(xhr.response);
          }
        } else {
          let errorMessage;
          try {
            const errorResponse = JSON.parse(xhr.response);
            errorMessage = errorResponse.message || `HTTP ${xhr.status}: ${xhr.statusText}`;
          } catch (e) {
            errorMessage = `HTTP ${xhr.status}: ${xhr.statusText}`;
          }
          reject(new Error(errorMessage));
        }
      };

      // Error handlers
      xhr.onerror = () => {
        console.error('Network error during upload');
        reject(new Error('Network error occurred. Please check your connection.'));
      };

      xhr.ontimeout = () => {
        console.error('Upload timeout');
        reject(new Error('Upload timeout. Please try again.'));
      };

      xhr.onabort = () => {
        console.error('Upload aborted');
        reject(new Error('Upload was aborted.'));
      };

      // Configure and send request
      xhr.open('POST', 'https://api.rtoxpert.com/api/service-requests', true);
      xhr.setRequestHeader('Authorization', `Bearer ${token}`);
      
      // Set longer timeout for large uploads (5 minutes)
      xhr.timeout = 300000; // 5 minutes
      
      console.log('Starting upload...');
      xhr.send(formDataToSend);
    });

    const response = await uploadPromise;
    console.log('Upload successful:', response);
    
    setShowPopup(true);
    setUploadProgress({});
    
    setTimeout(() => {
      setShowPopup(false);
      navigate("/profile");
    }, 3000);

  } catch (err) {
    console.error('Submit error:', err);
    
    let errorMessage = "Submission failed: ";
    
    // Handle specific error types
    if (err.message.includes('Network error') || err.message.includes('Failed to fetch')) {
      errorMessage += "Network connection failed. Please check your internet and try again.";
    } else if (err.message.includes('timeout')) {
      errorMessage += "Upload took too long. Please try with a better connection or smaller files.";
    } else if (err.message.includes('413') || err.message.includes('Request Entity Too Large')) {
      errorMessage += "Files are too large. Please reduce file sizes to under 50MB each and try again.";
    } else if (err.message.includes('400')) {
      errorMessage += "Invalid file format or data. Please check your uploads and try again.";
    } else if (err.message.includes('401') || err.message.includes('Unauthorized')) {
      errorMessage += "Authentication failed. Please refresh and try again.";
    } else if (err.message.includes('403') || err.message.includes('Forbidden')) {
      errorMessage += "Access denied. Please check your permissions.";
    } else if (err.message.includes('404')) {
      errorMessage += "Service not found. Please contact support.";
    } else if (err.message.includes('500')) {
      errorMessage += "Server error. Please try again later.";
    } else {
      errorMessage += err.message || "An unknown error occurred. Please try again.";
    }
    
    setError(errorMessage);
    setUploadProgress({});
  } finally {
    setIsSubmitting(false);
  }
};

  // File upload trigger function
  const triggerFileUpload = (fileType) => {
    if (fileInputRefs.current[fileType]) {
      fileInputRefs.current[fileType].click();
    }
  };

  // Camera Modal Component
  const CameraModal = () => (
    <div className={styles.cameraModal} onClick={(e) => e.target === e.currentTarget && closeCamera()}>
      <div className={styles.cameraContainer}>
        <div className={styles.cameraHeader}>
          <h3>Capture {currentCaptureType?.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())}</h3>
          <button className={styles.closeButton} onClick={closeCamera} aria-label="Close camera">
            ✕
          </button>
        </div>
        
        <div className={styles.videoContainer}>
          <div className={styles.cameraLoading}>
            <div className={styles.loadingSpinner}></div>
            <p>Starting camera...</p>
          </div>
          
          <video
            ref={videoRef}
            className={styles.video}
            autoPlay
            playsInline
            muted
            webkit-playsinline="true"
            onLoadedData={() => {
              console.log("Video loaded data");
              const loading = document.querySelector(`.${styles.cameraLoading}`);
              if (loading) loading.style.display = 'none';
            }}
            onError={(e) => {
              console.error("Video element error:", e);
              setError("Video playback failed. Please try again or use file upload.");
            }}
          />
          <canvas
            ref={canvasRef}
            className={styles.canvas}
            style={{ display: 'none' }}
          />
          
          {/* Camera overlay for better document alignment */}
          <div className={styles.cameraOverlay}>
            <div className={styles.documentFrame}>
              <div className={styles.frameCorner} style={{top: 0, left: 0}}></div>
              <div className={styles.frameCorner} style={{top: 0, right: 0}}></div>
              <div className={styles.frameCorner} style={{bottom: 0, left: 0}}></div>
              <div className={styles.frameCorner} style={{bottom: 0, right: 0}}></div>
            </div>
          </div>
        </div>
        
        <div className={styles.cameraInstructions}>
          <p>📋 Position the document within the frame</p>
          <p>💡 Ensure good lighting and focus</p>
        </div>
        
        <div className={styles.cameraControls}>
          <button 
            className={styles.captureButton}
            onClick={capturePhoto}
            aria-label="Capture photo"
          >
            📷 Capture
          </button>
          <button 
            className={styles.cancelButton}
            onClick={closeCamera}
            aria-label="Cancel camera"
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );

  // Document upload component
  const DocumentUpload = ({ label, fileType, required = true }) => (
    <div className={styles.documentUploadGroup}>
      <label>{label} {required && <span className={styles.required}>*</span>}</label>
      
      <div className={styles.uploadOptions}>
        <div className={styles.fileUploadWrapper}>
          <button
            type="button"
            className={styles.fileUploadLabel}
            onClick={() => triggerFileUpload(fileType)}
          >
            📁 Choose File
          </button>
          <input
            ref={el => fileInputRefs.current[fileType] = el}
            type="file"
            className={styles.fileInput}
            onChange={(e) => handleFileChange(e, fileType)}
            accept="image/*,.heic,.heif"
            style={{ display: 'none' }}
          />
        </div>
        
        {cameraSupported && (
          <button
            type="button"
            className={styles.cameraButton}
            onClick={() => startCamera(fileType)}
            aria-label={`Open camera for ${label}`}
          >
            📷 Camera
          </button>
        )}
      </div>
      
      {files[fileType] && (
        <div className={styles.fileItem}>
          {previews[fileType] && (
            <img 
              src={previews[fileType]} 
              alt={`Preview of ${label}`}
              className={styles.filePreview}
            />
          )}
          <div className={styles.fileInfo}>
            <span className={styles.fileName}>📄 {files[fileType].name}</span>
            <span className={styles.fileSize}>
              ({(files[fileType].size / 1024 / 1024).toFixed(2)} MB)
            </span>
          </div>
          <button 
            type="button"
            className={styles.removeFileButton}
            onClick={() => removeFile(fileType)}
            aria-label={`Remove ${label}`}
          >
            ✕
          </button>
        </div>
      )}
    </div>
  );

  return (
    <div className={styles.container}>
      <div className={styles.formSection}>
        {error && (
          <div className={styles.errorMessage} role="alert">
            {error}
            <button 
              className={styles.dismissError}
              onClick={() => setError(null)}
              aria-label="Dismiss error"
            >
              ✕
            </button>
          </div>
        )}
        
        {!cameraSupported && (
          <div className={styles.warningMessage}>
            📱 Camera not available. Please use file upload instead.
          </div>
        )}

        <div className={styles.inputGroup}>
          <label>Name <span className={styles.required}>*</span></label>
          <input
            name="name"
            type="text"
            placeholder="Enter your full name"
            className={styles.inputField}
            value={formData.name}
            onChange={handleChange}
            required
          />
        </div>

        <div className={styles.inputGroup}>
          <label>Aadhar Registered Mobile Number <span className={styles.required}>*</span></label>
          <input
            name="registeredMobileNumber"
            type="tel"
            placeholder="10-digit mobile number"
            className={styles.inputField}
            value={formData.registeredMobileNumber}
            onChange={handleChange}
            maxLength="10"
            pattern="[6-9][0-9]{9}"
            required
          />
        </div>

        <DocumentUpload label="RC - Front Side" fileType="rcFront" />
        <DocumentUpload label="RC - Back Side" fileType="rcBack" />
        <DocumentUpload label="Aadhar Card - Front Side" fileType="aadharFront" />
        <DocumentUpload label="Aadhar Card - Back Side" fileType="aadharBack" />

        {uploadProgress.overall > 0 && (
          <div className={styles.progressContainer}>
            <div className={styles.progressBar}>
              <div 
                className={styles.progressFill}
                style={{ width: `${uploadProgress.overall}%` }}
              ></div>
            </div>
            <span className={styles.progressText}>Uploading... {uploadProgress.overall}%</span>
          </div>
        )}

        <button 
          className={`${styles.submitButton} ${isSubmitting ? styles.submitting : ''}`}
          onClick={handleSubmit}
          disabled={isSubmitting}
        >
          {isSubmitting ? (
            <>
              <div className={styles.loadingSpinner}></div>
              Submitting...
            </>
          ) : (
            'Submit Request'
          )}
        </button>

        {showPopup && (
          <div className={styles.successMessage} role="alert">
            <div className={styles.successIcon}>✅</div>
            <h3>Request Submitted Successfully!</h3>
            <p>Our agent will reach out to you soon.</p>
          </div>
        )}
      </div>

      <div className={styles.imageSection}>
        <img
          src={carBikeImage}
          alt="Vehicle services illustration"
          className={styles.vehicleImage}
        />
      </div>

      {showCamera && <CameraModal />}
    </div>
  );
}

export default AdhaarVerification;